
import { useState } from 'react';
import AnimatedSection from '../base/AnimatedSection';

const ROICalculator = () => {
  const [monthlyRevenue, setMonthlyRevenue] = useState('');
  const [conversionRate, setConversionRate] = useState('');
  const [monthlyVisitors, setMonthlyVisitors] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [email, setEmail] = useState('');
  const [showEmailForm, setShowEmailForm] = useState(false);

  const calculateROI = () => {
    const revenue = parseFloat(monthlyRevenue) || 0;
    const currentRate = parseFloat(conversionRate) || 0;
    const visitors = parseFloat(monthlyVisitors) || 0;

    // Conservative improvement estimates
    const improvedRate = currentRate * 1.5; // 50% improvement
    const currentConversions = (visitors * currentRate) / 100;
    const improvedConversions = (visitors * improvedRate) / 100;
    const additionalConversions = improvedConversions - currentConversions;
    
    const avgOrderValue = revenue / (currentConversions || 1);
    const additionalRevenue = additionalConversions * avgOrderValue;
    const annualIncrease = additionalRevenue * 12;

    return {
      currentConversions: Math.round(currentConversions),
      improvedConversions: Math.round(improvedConversions),
      additionalRevenue: Math.round(additionalRevenue),
      annualIncrease: Math.round(annualIncrease),
      roi: Math.round((annualIncrease / 3500) * 100) // Against First Class package
    };
  };

  const handleCalculate = () => {
    if (monthlyRevenue && conversionRate && monthlyVisitors) {
      setShowResults(true);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const results = calculateROI();
    const formData = new FormData();
    formData.append('email', email);
    formData.append('monthly_revenue', monthlyRevenue);
    formData.append('conversion_rate', conversionRate);
    formData.append('monthly_visitors', monthlyVisitors);
    formData.append('projected_annual_increase', results.annualIncrease.toString());
    formData.append('projected_roi', `${results.roi}%`);
    
    try {
      await fetch('https://readdy.ai/api/form/d46fa2jhnd8fvojd2430', {
        method: 'POST',
        body: formData
      });
      
      setShowEmailForm(false);
      alert('Thanks! Your detailed ROI report is on its way.');
    } catch (error) {
      console.error('Error submitting calculator results:', error);
    }
  };

  const results = showResults ? calculateROI() : null;

  return (
    <AnimatedSection className="bg-gradient-to-br from-[#708474]/10 to-[#a4ae9e]/10 rounded-2xl p-8">
      <div className="text-center mb-8">
        <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4">
          <i className="ri-calculator-line text-4xl text-[#708474]"></i>
        </div>
        <h3 className="text-2xl font-bold text-[#34444b] mb-2">Copy ROI Calculator</h3>
        <p className="text-[#73807c]">See what better copy could mean for your bottom line</p>
      </div>

      {!showResults ? (
        <div className="max-w-md mx-auto space-y-6">
          <div>
            <label className="block text-sm font-medium text-[#34444b] mb-2">
              Monthly Revenue ($)
            </label>
            <input
              type="number"
              value={monthlyRevenue}
              onChange={(e) => setMonthlyRevenue(e.target.value)}
              placeholder="50000"
              className="w-full px-4 py-3 border border-[#a4ae9e] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#708474] text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#34444b] mb-2">
              Current Conversion Rate (%)
            </label>
            <input
              type="number"
              step="0.1"
              value={conversionRate}
              onChange={(e) => setConversionRate(e.target.value)}
              placeholder="2.5"
              className="w-full px-4 py-3 border border-[#a4ae9e] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#708474] text-sm"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#34444b] mb-2">
              Monthly Website Visitors
            </label>
            <input
              type="number"
              value={monthlyVisitors}
              onChange={(e) => setMonthlyVisitors(e.target.value)}
              placeholder="5000"
              className="w-full px-4 py-3 border border-[#a4ae9e] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#708474] text-sm"
            />
          </div>

          <button
            onClick={handleCalculate}
            disabled={!monthlyRevenue || !conversionRate || !monthlyVisitors}
            className="w-full bg-[#708474] text-white py-3 rounded-lg hover:bg-[#73807c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
          >
            Calculate My ROI
          </button>
        </div>
      ) : (
        <div className="max-w-2xl mx-auto">
          {!showEmailForm ? (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg p-6 border border-[#a4ae9e]/30">
                  <h4 className="font-semibold text-[#34444b] mb-2">Current Performance</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#73807c]">Monthly Conversions:</span>
                      <span className="font-medium">{results?.currentConversions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#73807c]">Conversion Rate:</span>
                      <span className="font-medium">{conversionRate}%</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-[#708474]/10 to-[#a4ae9e]/10 rounded-lg p-6 border border-[#708474]/30">
                  <h4 className="font-semibold text-[#34444b] mb-2">With Better Copy</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[#73807c]">Monthly Conversions:</span>
                      <span className="font-medium text-[#708474]">{results?.improvedConversions}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[#73807c]">Conversion Rate:</span>
                      <span className="font-medium text-[#708474]">{(parseFloat(conversionRate) * 1.5).toFixed(1)}%</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-[#708474] to-[#73807c] rounded-lg p-6 text-white text-center">
                <h4 className="text-xl font-bold mb-4">Your Potential Results</h4>
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <div className="text-2xl font-bold">${results?.additionalRevenue.toLocaleString()}</div>
                    <div className="text-sm opacity-90">Additional Monthly Revenue</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">${results?.annualIncrease.toLocaleString()}</div>
                    <div className="text-sm opacity-90">Annual Revenue Increase</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{results?.roi}%</div>
                    <div className="text-sm opacity-90">ROI on Investment</div>
                  </div>
                </div>
              </div>

              <div className="text-center">
                <p className="text-[#73807c] mb-4">
                  Want a detailed breakdown and action plan?
                </p>
                <button
                  onClick={() => setShowEmailForm(true)}
                  className="bg-[#708474] text-white px-8 py-3 rounded-lg hover:bg-[#73807c] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Get My Detailed Report
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center">
              <h4 className="text-xl font-bold text-[#34444b] mb-4">Get Your Detailed ROI Report</h4>
              <p className="text-[#73807c] mb-6">
                We'll send you a personalized action plan to achieve these results.
              </p>
              
              <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto" data-readdy-form>
                <div className="flex gap-4">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="flex-1 px-4 py-3 border border-[#a4ae9e] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#708474] text-sm"
                  />
                  <button
                    type="submit"
                    className="bg-[#708474] text-white px-6 py-3 rounded-lg hover:bg-[#73807c] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Send Report
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      )}
    </AnimatedSection>
  );
};

export default ROICalculator;
